import {
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    OnInit,
    Output,
    ViewChild,
    effect,
    input,
    signal,
    inject,
} from '@angular/core';

import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { filterNil } from 'ngxtension/filter-nil';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    fromEvent,
    map,
    tap,
    switchMap,
    Observable,
    from,
    catchError,
} from 'rxjs';
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';
import 'froala-editor/js/plugins.pkgd.min.js';
import {
    ContentAsset,
    DataMartMergeField,
    DataMartMergeFieldResponse,
    FieldType,
    FroalaEditorOptions,
    FroalaFieldMergeDeletedEventData,
    FroalaFieldMergeDroppedEventData,
    ImageAssetRef,
} from '@r1-compass-library/models';
import { DraggableDirective } from '@r1-compass-library/directives/draggable.directive';
import { DynamicContentStore } from '../plays/play-talking-points/dynamic-content.store';
import { AppConstants } from '../app.constants';
import { FroalaSetupService } from '../../services/froala-setup.service';
import { createNotifier, formValueSignal } from '@r1-compass-library/utilities';
import { FileTransferService, SnackBarService } from '@r1-compass-library/services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FroalaEventsService } from '@r1-compass-library/services/froala/froala-events.service';
import { FroalaImagesService } from '@r1-compass-library/services/froala/froala-images.service';
import { FroalaImageUploadService } from '@r1-compass-library/services/froala/froala-image-upload.service';

export type MergeFieldSourceOption = {
    displayName: string;
    name: string;
    disabled?: boolean;
};

type MergeField = {
    id: string;
    displayName: string;
    class: string;
    dataContentType: string;
    dataContentOptions?: string;
    fieldType?: FieldType;
    enabled?: boolean;
    valid?: boolean;
};

export type MergeFieldOption = {
    sourceName?: string;
    mergeFieldData: MergeField[];
};

@Component({
    selector: 'r1-compass-app-content-editor',
    imports: [
        DraggableDirective,
        FormsModule,
        FroalaEditorModule,
        FroalaViewModule,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        ReactiveFormsModule,
    ],
    templateUrl: './content-editor.component.html',
    styleUrl: './content-editor.component.scss',
})
export class ContentEditorComponent implements OnInit {
    readonly #froalaSetupService = inject(FroalaSetupService);
    readonly #froalaEventsService = inject(FroalaEventsService);
    readonly #froalaImagesService = inject(FroalaImagesService);
    readonly #froalaImageClipboardService = inject(FroalaImageUploadService);
    readonly #fileTransferService = inject(FileTransferService);
    readonly #snackBarService = inject(SnackBarService);
    protected readonly dynamicContentStore = inject(DynamicContentStore);
    protected readonly destroyRef = inject(DestroyRef);

    froalaContentFormControl = input.required<FormControl<string | null>>();
    editorContentType = input.required<'play' | 'content'>();
    fileImporterHeaders = input.required<string[]>();
    showErrors = input<boolean>(false);
    assetId = input.required<string>();

    @Output()
    fieldMergeDeleted = new EventEmitter<FroalaFieldMergeDeletedEventData>();

    @Output()
    fieldMergeDropped = new EventEmitter<FroalaFieldMergeDroppedEventData>();

    sourceFormControl: FormControl<string | MergeFieldSourceOption> = new FormControl('', { nonNullable: true });
    sourceInputValue = formValueSignal<string | MergeFieldSourceOption>(this.sourceFormControl);
    searchFormControl: FormControl<string> = new FormControl('', { nonNullable: true });
    searchInputValue = formValueSignal<string>(this.searchFormControl);

    froalaOptions: FroalaEditorOptions = new FroalaEditorOptions();

    @ViewChild('editorContainer', { static: true }) editorContainer!: ElementRef<HTMLDivElement>;
    @ViewChild('searchInput', { static: true }) fieldMergeSearchInput!: ElementRef;

    selectedMergeFieldOption = signal<MergeFieldOption | undefined>(undefined);

    // Tracks whether a genuine user modification occurred, distinguishing actual edits from
    // synthetic Froala DOM normalizations or wrapper directive events that would falsely trigger the save guard.
    #hasUserInteracted = false;

    // One effect needs a notifier on an event, akin to a Subject.
    selectedSourceFieldMergeFilterNotify = createNotifier();

    protected sourceOptions = signal<MergeFieldSourceOption[]>([]);
    protected fieldMergeOptions = signal<MergeFieldOption[]>([]);

    onSourceSelected(event: MatAutocompleteSelectedEvent) {
        const allSourceOptions = this.getAllSourceOptions();
        allSourceOptions.forEach(option => (option.disabled = false));
        this.sourceOptions.set(allSourceOptions);
        this.searchFormControl.setValue('');

        const selected: MergeFieldSourceOption = event.option.value;
        if (selected.displayName === AppConstants.FILE_HEADER_DISPLAY_NAME) {
            const option: MergeFieldOption = {
                sourceName: selected.displayName,
                mergeFieldData: this.fileImporterHeaders()
                    .map(header => this.fileHeaderToFieldMergeOption(header))
                    .sort((a, b) => a.displayName!.localeCompare(b.displayName!) ?? 0),
            };
            this.selectedMergeFieldOption.set(option);
            this.fieldMergeOptions.set([option]);
        } else if (selected.displayName === AppConstants.LIBRARY_CONTENT_DISPLAY_NAME) {
            const option = {
                sourceName: selected.displayName,
                mergeFieldData: this.dynamicContentStore
                    .libraryContent()
                    .map(asset => this.contentAssetToMergeFieldOption(asset))
                    .sort((a, b) => a.displayName!.localeCompare(b.displayName!) ?? 0)
                    .filter(asset => asset.enabled && asset.valid),
            };
            this.selectedMergeFieldOption.set(option);
            this.fieldMergeOptions.set([option]);
        } else {
            this.dynamicContentStore.setMergeFields([]);
            this.dynamicContentStore.mergeFields$.subscribe(fields => {
                const data: DataMartMergeFieldResponse = {
                    tableName: selected.name,
                    tableDisplayName: selected.displayName,
                    fields: fields,
                };
                const option = this.fieldMergeResponseToSearchOption([data]).at(0)!;
                option.mergeFieldData.sort((a, b) => a.displayName!.localeCompare(b.displayName!) ?? 0);
                this.selectedMergeFieldOption.set(option);
                this.fieldMergeOptions.set([option]);
            });
            this.dynamicContentStore.getMergeFields(selected.name);
        }
    }

    constructor() {
        // FroalaInteractionCallbacks decouples Froala editor events from Angular form state,
        // notifying the component when user interactions or synthetic editor events occur.
        this.froalaOptions = this.#froalaSetupService.setupFroalaEditor({
            onUserInteracted: () => {
                this.#hasUserInteracted = true;
            },
            onEditorMousedown: () => {
                this.#resetUntouchedIfNoInteraction();
            },
            onEditorFocus: () => {
                this.#resetUntouchedIfNoInteraction();
            },
            onContentChanged: () => {
                this.#resetPristineAndUntouchedIfNoInteraction();
            },
        });

        this.froalaOptions.fieldMergeDeleted = data => {
            this.#hasUserInteracted = true;
            this.froalaContentFormControl().markAsDirty();
            this.froalaContentFormControl().markAsTouched();
            this.fieldMergeDeleted.emit(data);
        };
        this.froalaOptions.fieldMergeDropped = data => {
            this.#hasUserInteracted = true;
            this.froalaContentFormControl().markAsDirty();
            this.froalaContentFormControl().markAsTouched();
            this.fieldMergeDropped.emit(data);
        };

        this.#froalaEventsService.attachImageErrorBehavior(this.froalaOptions, this.#froalaImagesService);
        this.#froalaEventsService.attachImageUploadBehavior(this.froalaOptions, this.#froalaImageClipboardService, {
            uploadImage$: (file, fileName) => this.#uploadImageToS3(file, fileName),
            onUploadSuccess: () => this.#markInteractedAndDirty(),
            onImageRemoved: () => this.#markInteractedAndDirty(),
            onUploadError: fileName => this.#showUploadErrorSnackbar(fileName),
            getAssetId: () => this.assetId(),
            getAssetType: () => this.editorContentType(),
            getAssetRef: () => ({ assetId: this.assetId(), assetType: this.editorContentType() }),
            copyImageToCurrentAsset$: (source, target, pastedSrc) =>
                this.#copyImageToCurrentAsset(source, target, pastedSrc),
        });

        this.#listenForPristineReset();

        effect(
            () => {
                const fileHeaders = this.fileImporterHeaders();
                const libraryContent = this.dynamicContentStore.availableLibraryContent();
                const searchResults = this.dynamicContentStore.searchResults();
                const searchValue = this.searchInputValue().toLocaleLowerCase();

                const crossSourceFieldMerges = this.calculateCrossSourceFieldMerges(
                    fileHeaders,
                    libraryContent,
                    searchResults,
                    searchValue
                );

                this.fieldMergeOptions.set(crossSourceFieldMerges);
            },
            { debugName: 'calculateCrossSourceFieldMerges' }
        );

        effect(
            () => {
                this.selectedSourceFieldMergeFilterNotify.listen();
                const selectedOption = this.selectedMergeFieldOption();
                const searchValue = this.searchInputValue();

                const clone = this.filterFieldMergesForSelectedSource(selectedOption, searchValue);

                this.fieldMergeOptions.set(clone);
            },
            { debugName: 'filterFieldMergesForSelectedSource' }
        );

        effect(
            () => {
                const mergeFieldOptions = this.fieldMergeOptions();
                const searchFormValue = this.searchInputValue();
                const filterVal = this.sourceInputValue();

                const sources = this.calculateSourceOptions(filterVal, mergeFieldOptions, searchFormValue);

                this.sourceOptions.set(sources);
            },
            { debugName: 'calculateSourceOptions' }
        );
    }

    ngOnInit(): void {
        this.#attachEditorContainerInteractionListeners();
        this.#attachFroalaBlurHandler();

        this.dynamicContentStore.reset();
        this.dynamicContentStore.getMergeFieldTables();
        this.subscribeForMergeFieldSearchInput();
    }

    /**
     * Capture-phase (`useCapture = true`) DOM listeners intercept merge field drops and delete clicks
     * before they reach Froala. Because dropping or deleting mutates the DOM immediately and triggers
     * Froala's contentChanged before bubbling handlers run, capture phase ensures #hasUserInteracted
     * and dirty state are set prior to contentChanged, preventing #resetPristineAndUntouchedIfNoInteraction()
     * from inadvertently reverting the form to pristine. Kept in component to manage DOM element and DestroyRef lifecycle.
     */
    #attachEditorContainerInteractionListeners(): void {
        const editorContainerEl = this.editorContainer?.nativeElement;
        if (!editorContainerEl) {
            return;
        }

        const markInteractedAndDirty = () => {
            this.#hasUserInteracted = true;
            this.froalaContentFormControl().markAsDirty();
            this.froalaContentFormControl().markAsTouched();
        };

        const onDropCapture = () => {
            markInteractedAndDirty();
        };

        const onClickCapture = (event: MouseEvent) => {
            if ((event.target as HTMLElement | null)?.closest('.field-merge-remove')) {
                markInteractedAndDirty();
            }
        };

        editorContainerEl.addEventListener('drop', onDropCapture, true);
        editorContainerEl.addEventListener('click', onClickCapture, true);

        this.destroyRef.onDestroy(() => {
            editorContainerEl.removeEventListener('drop', onDropCapture, true);
            editorContainerEl.removeEventListener('click', onClickCapture, true);
        });
    }

    /**
     * Synchronizes the interaction flag when the form control is externally reset or marked pristine (e.g. after save).
     */
    #listenForPristineReset(): void {
        effect(() => {
            const control = this.froalaContentFormControl();
            control.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
                if (control.pristine) {
                    this.#hasUserInteracted = false;
                }
            });
        });
    }

    /**
     * Angular-froala-wysiwyg schedules `onTouched()` via a macrotask (`setTimeout(..., 0)`) on mousedown.
     * We reset synchronously and schedule a macrotask to ensure untouched state is preserved after the directive runs.
     */
    #resetUntouchedIfNoInteraction(): void {
        if (!this.#hasUserInteracted) {
            this.froalaContentFormControl().markAsUntouched();
            setTimeout(() => {
                if (!this.#hasUserInteracted) {
                    this.froalaContentFormControl().markAsUntouched();
                }
            }, 0);
        }
    }

    /**
     * When Froala normalizes DOM on focus/init (e.g. inserting `<p><br></p>`), it emits synthetic contentChanged events.
     * We reset across the event loop lifecycle when no real user interaction occurred:
     * 1. Synchronously: Reverts immediate form mutations from the current event loop turn.
     * 2. Microtask (`queueMicrotask`): Reverts deferred dirty status propagated by Angular reactive forms/signals before navigation checks run.
     * 3. Macrotask (`setTimeout 0`): Clears touched status after angular-froala-wysiwyg's internal `setTimeout(onTouched, 0)` fires.
     */
    #resetPristineAndUntouchedIfNoInteraction(): void {
        if (!this.#hasUserInteracted) {
            this.froalaContentFormControl().markAsPristine();
            this.froalaContentFormControl().markAsUntouched();
            queueMicrotask(() => {
                if (!this.#hasUserInteracted) {
                    this.froalaContentFormControl().markAsPristine();
                    this.froalaContentFormControl().markAsUntouched();
                }
            });
            setTimeout(() => {
                if (!this.#hasUserInteracted) {
                    this.froalaContentFormControl().markAsPristine();
                    this.froalaContentFormControl().markAsUntouched();
                }
            }, 0);
        }
    }

    /**
     * Froala is a custom widget and does not call markAsTouched() on the bound FormControl when the editor loses
     * focus. Without this, the control's `touched` state never becomes true through normal user interaction,
     * which means any error display gated on `touched && invalid` would never trigger.
     * We extend the existing blur event (if any) rather than replacing it outright.
     */
    #attachFroalaBlurHandler(): void {
        const existingBlur = this.froalaOptions.events?.['blur'];
        this.froalaOptions.events = {
            ...this.froalaOptions.events,
            blur: (e: any, editor: any) => {
                if (typeof existingBlur === 'function') {
                    existingBlur.call(editor, e as unknown as never, editor as never);
                }
                this.froalaContentFormControl().markAllAsTouched();
            },
        };
    }

    /**
     * When a key is entered into the search box, the resulting action depends on if a source is selected.
     * If no source is selected, then call the datamart to search for fields that match the search term.
     * Else, filter the existing field-merges in the list
     */
    private subscribeForMergeFieldSearchInput() {
        fromEvent<KeyboardEvent>(this.fieldMergeSearchInput.nativeElement, 'keyup')
            .pipe(
                map(event => {
                    if (typeof this.sourceInputValue() === 'string') {
                        return event; // Source option is not selected, continue
                    } else {
                        this.selectedSourceFieldMergeFilterNotify.notify(); // Source option is currently selected, notify signal for filtering
                        return null;
                    }
                }),
                filterNil(),
                tap(() => this.resetForEmptySearchBox()),
                filter(event => !!event.key.match(/^\S$/) && !event.ctrlKey && !event.altKey && !event.metaKey), // filter for all character keys, without modifier keys
                debounceTime(300),
                map((event: KeyboardEvent) => (event.target as HTMLInputElement).value),
                filter(value => value.length > 1),
                distinctUntilChanged()
            )
            .subscribe(value => {
                if (value.trim()) {
                    this.sourceFormControl.setValue('');
                    this.dynamicContentStore.searchMergeFields(value);
                }
            });
    }

    /**
     * When the search box becomes empty, re-enable all source options, and reset the merge field options.
     */
    private resetForEmptySearchBox() {
        if (this.searchFormControl.value.length === 0) {
            const currSourceOptions = [...this.sourceOptions()];
            currSourceOptions.forEach(option => (option.disabled = false));
            this.sourceOptions.set(currSourceOptions);
            this.fieldMergeOptions.set([]);
        }
    }

    onFieldMergeContainerScroll(event: any) {
        if (this.sourceFormControl.value) {
            return; // Don't do field-merge search if a value is selected
        }
        if (event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight - 1) {
            const searchTerm = this.searchFormControl.value.trim();
            this.dynamicContentStore.searchMergeFields(searchTerm);
        }
    }

    displayFn(option: MergeFieldSourceOption): string {
        return option && option.displayName ? option.displayName : '';
    }

    private getAllSourceOptions() {
        const fileHeaders = this.fileImporterHeaders();
        const libraryContent = this.dynamicContentStore.libraryContent();
        const sources: MergeFieldSourceOption[] = [];
        if (fileHeaders.length) {
            sources.push({
                displayName: AppConstants.FILE_HEADER_DISPLAY_NAME,
                name: AppConstants.CONTENT_TYPE_FILE_HEADER,
            });
        }
        if (libraryContent.some(asset => asset.enabled && asset.valid)) {
            sources.push({
                displayName: AppConstants.LIBRARY_CONTENT_DISPLAY_NAME,
                name: AppConstants.CONTENT_TYPE_DYNAMIC_CONTENT,
            });
        }
        sources.push(...this.dynamicContentStore.mergeFieldTables());
        return sources.sort((a, b) => a.displayName!.localeCompare(b.displayName!) ?? 0);
    }

    protected fileHeadersToSearchOption(fileHeaders: string[]): MergeFieldOption {
        return {
            sourceName: AppConstants.FILE_HEADER_DISPLAY_NAME,
            mergeFieldData: fileHeaders.map(header => this.fileHeaderToFieldMergeOption(header)),
        };
    }

    protected fileHeaderToFieldMergeOption(header: string): MergeField {
        return {
            id: header,
            displayName: header,
            class: AppConstants.FIELD_MERGE_CLASS_FILE_HEADER,
            dataContentType: AppConstants.CONTENT_TYPE_FILE_HEADER,
        };
    }

    protected fieldMergeResponseToSearchOption(mergeFieldResponses: DataMartMergeFieldResponse[]): MergeFieldOption[] {
        return mergeFieldResponses.map(res => ({
            sourceName: res.tableDisplayName,
            mergeFieldData: res.fields.map(field => {
                field.tableName = field.tableName ?? res.tableName;
                return this.fieldMergeToFieldMergeOption(field);
            }),
        }));
    }

    protected fieldMergeToFieldMergeOption(fieldMerge: DataMartMergeField): MergeField {
        return {
            id: fieldMerge.name,
            displayName: fieldMerge.displayName,
            dataContentType: AppConstants.CONTENT_TYPE_TFS,
            class: AppConstants.FIELD_MERGE_CLASS_TFS,
            dataContentOptions: JSON.stringify(fieldMerge),
            fieldType: fieldMerge.fieldType,
        };
    }

    protected contentAssetToSearchOption(contentAssets: ContentAsset[]): MergeFieldOption {
        return {
            sourceName: AppConstants.LIBRARY_CONTENT_DISPLAY_NAME,
            mergeFieldData: contentAssets.map(asset => this.contentAssetToMergeFieldOption(asset)),
        };
    }

    protected contentAssetToMergeFieldOption(asset: ContentAsset): MergeField {
        return {
            id: String(asset.id),
            displayName: asset.name,
            dataContentType: AppConstants.CONTENT_TYPE_DYNAMIC_CONTENT,
            class: AppConstants.FIELD_MERGE_CLASS_DYNAMIC_CONTENT,
            dataContentOptions: JSON.stringify({
                id: asset.id,
            }),
            enabled: asset.enabled ?? false,
            valid: asset.valid ?? false,
        };
    }

    /**
     * @description Prevents the event from propagating further up the DOM tree into froala causing weird stuff like field merges being deleted.
     * @link https://relationshipone.atlassian.net/browse/CLOUD-10019 (Talking Points - pressing enter causes unexpected results #14735)
     * @description
     * For some weird reason, pressing `Enter` while inside the field searching inputs causes the Froala editor to
     * delete the first field merge. We do not have a need for pressing `Enter` in these inputs, so we just stop
     * the propagation and the default behavior of form fields like this which cause this issue in the Froala form.
     */
    protected doNotPropogateEnter(event: Event) {
        event.stopPropagation();
        event.preventDefault();
    }

    // Public for easy testing sake
    public calculateCrossSourceFieldMerges(
        fileHeaders: string[],
        libraryContent: ContentAsset[],
        searchResults: DataMartMergeFieldResponse[],
        searchValue: string
    ): MergeFieldOption[] {
        if (searchValue === '') {
            return [];
        }

        const options: MergeFieldOption[] = [];
        const matchedHeaders = fileHeaders.filter(header => header.toLocaleLowerCase().includes(searchValue));
        if (matchedHeaders.length) {
            options.push(this.fileHeadersToSearchOption(matchedHeaders.sort((a, b) => a.localeCompare(b) ?? 0)));
        }
        const matchedAssets = libraryContent.filter(asset => asset.name.toLocaleLowerCase().includes(searchValue));
        if (matchedAssets.length) {
            options.push(
                this.contentAssetToSearchOption(matchedAssets.sort((a, b) => a.name!.localeCompare(b.name!) ?? 0))
            );
        }
        options.push(...this.fieldMergeResponseToSearchOption(searchResults));
        return options;
    }

    private calculateSourceOptions(
        filterVal: string | MergeFieldSourceOption,
        mergeFieldOptions: MergeFieldOption[],
        searchFormValue: string
    ): MergeFieldSourceOption[] {
        if (typeof filterVal !== 'string') {
            return [];
        }

        let sources = this.getAllSourceOptions();
        if (searchFormValue) {
            sources.forEach(source => {
                source.disabled = !mergeFieldOptions.find(
                    option => option.sourceName?.toLocaleLowerCase() === source.displayName.toLocaleLowerCase()
                );
            });
        }
        sources = sources.filter(source =>
            source.displayName.toLocaleLowerCase().includes(filterVal.toLocaleLowerCase())
        );
        sources.sort((a, b) => a.displayName!.localeCompare(b.displayName!) ?? 0);
        return sources;
    }

    private filterFieldMergesForSelectedSource(
        selectedOption: MergeFieldOption | undefined,
        searchValue: string
    ): MergeFieldOption[] {
        const clone = structuredClone(selectedOption);
        if (clone) {
            clone.mergeFieldData = clone?.mergeFieldData.filter(field =>
                field.displayName.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())
            );

            return [clone];
        } else {
            return [];
        }
    }

    #markInteractedAndDirty(): void {
        this.#hasUserInteracted = true;
        this.froalaContentFormControl().markAsDirty();
        this.froalaContentFormControl().markAsTouched();
    }

    #uploadImageToS3(file: File, fileName: string): Observable<{ url: string }> {
        return this.#fileTransferService.getSignedUrl(fileName, this.assetId(), this.editorContentType()).pipe(
            filter(res => !!res?.url),
            switchMap(res =>
                this.#fileTransferService
                    .uploadFile(file, res.url, res.contentType)
                    .pipe(map(() => ({ url: res.url.split('?')[0] })))
            ),
            takeUntilDestroyed(this.destroyRef)
        );
    }

    #showUploadErrorSnackbar(fileName: string): void {
        this.#snackBarService.openGenericSnackbar({
            messages: ['Failed to upload image: ' + fileName],
            panelClass: 'generic-snack-bar-warning',
            duration: 10000,
            showDismissButton: true,
            verticalPosition: 'top',
        });
    }

    #copyImageToCurrentAsset(
        source: ImageAssetRef & { fileName: string },
        targetName: string,
        pastedSrc: string
    ): Observable<File> {
        const readBlob = (url: string, cache: RequestCache) =>
            fetch(url, { mode: 'cors', cache }).then(r => {
                if (!r.ok) {
                    throw new Error(`GET ${r.status}`);
                } // otherwise the 403/404 XML body gets uploaded as the image
                return r.blob();
            });

        // 1) read the pasted URL / local blob once; 2) fall back to a fresh presigned GET for the source asset
        const bytes$ = from(readBlob(pastedSrc, 'force-cache')).pipe(
            catchError(() =>
                this.#fileTransferService.getSignedUrl(source.fileName, source.assetId, source.assetType, 'GET').pipe(
                    filter(res => !!res?.url),
                    switchMap(res => from(readBlob(res.url, 'no-store')))
                )
            )
        );

        return bytes$.pipe(
            map(blob => new File([blob], targetName, { type: blob.type || 'image/png' })),
            switchMap(file => this.#uploadImageToS3(file, targetName).pipe(map(() => file))),
            takeUntilDestroyed(this.destroyRef)
        );
    }
}
